import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useCatalogContext } from "../../catalog/hooks/useCatalogContext";
import type { CatalogItemsFilterConfig } from "../../catalog/posCard";
import { getUniqueCartItemIds } from "../../helpers/saleHelpers";
import { createRecommendationInfo } from "../attribution";
import { selectRecommendedItems } from "../selection";
import { useRecommendationsShown } from "./useRecommendationsShown";
import {
  getSuggestiveSellingProvider,
  isSuggestiveSellingAvailable,
  selectIsFetchingRecommendations,
  selectRecommendations,
} from "../selectors";
import type { RecommendationInfo, RecommendedItem } from "../types";

/** One frozen empty set, so "no categories accepted yet" is always the same reference. */
const EMPTY_CATEGORIES: ReadonlySet<string> = new Set<string>();

/**
 * `useRecommendations` — THE headless entry point for any surface that shows suggestions.
 *
 * It owns everything that is not pixels: eligibility, the cart and category exclusions, the
 * catalogue projection, the "shown" analytics, and the per-round category memory. A caller
 * renders `items` however it likes — a horizontal strip on the POS, a vertical rail on a wide
 * screen, a full-bleed carousel on a kiosk — and calls `onItemSelected` when the guest picks one.
 *
 * ## The category rule, and why it needs a ref
 *
 * The engine answers once and the operator may take several items from that one answer. Without
 * a rule, accepting "Coke" leaves the other two cold drinks on screen. So an accepted item's
 * classification category is remembered and its siblings drop out — until the next engine
 * response, when the memory is cleared. That memory is per-round UI state, not store state,
 * which is why it is a ref reset on `recommendationId` rather than a slice.
 *
 * ⚠️ `onItemSelected` must NOT record the category when the click opened a customiser: the item
 * is not in the cart yet and the guest may cancel. The customiser's own success callback calls
 * `onItemAdded` instead. Getting this wrong makes suggestions vanish on a cancelled dialog.
 *
 * ## What this hook deliberately does NOT do
 *
 * It does not mirror the visible list anywhere. The POS writes it to local storage so its cart
 * analytics can tell "added from the catalogue while a suggestion for it was on screen" apart from
 * a plain catalogue add — but that rule depends on whether the WIDGET IS OPEN, which is the
 * surface's business, not the flow's. A surface that wants the mirror runs its own effect on
 * `items`.
 */
export interface UseRecommendationsOptions {
  /** How many suggestions this surface has room for. Defaults to 3. */
  limit?: number;
  /** Override the catalogue filters. Defaults to all six on — see `RECOMMENDATION_CATALOG_FILTER`. */
  catalogFilter?: CatalogItemsFilterConfig;
  /**
   * Drop a second suggestion from a category the guest already took one from. On by default;
   * a surface with room for a whole rail may want it off.
   */
  excludeAcceptedCategories?: boolean;
  /**
   * Report `RECOMMENDATIONS_SHOWN` once per engine response. On by default.
   *
   * 🔴 **A COLLAPSIBLE surface must pass `false` and call `useRecommendationsShown` itself**,
   * after its collapse state is known — otherwise a folded widget reports impressions for
   * suggestions nobody can see. This hook cannot do it: the collapse machine depends on
   * `hasItems`, so it necessarily runs *after* this hook. See `Containers/NewOrder` for the shape.
   */
  trackShown?: boolean;
  /**
   * Extra gate on top of the account's own eligibility — e.g. "only on the ordering screen".
   * When `false`, the hook returns no items and fires no analytics.
   */
  enabled?: boolean;
}

export interface UseRecommendationsResult {
  /** Ready to render, already capped and filtered. */
  items: RecommendedItem[];
  hasItems: boolean;
  /** The engine call is in flight — show the "generating" state. */
  isFetching: boolean;
  /** Suggestive selling is on for this account and the terminal is online. */
  isAvailable: boolean;
  recommendationId?: string;
  provider?: string;
  /** The attribution to stamp on anything added from this surface. */
  recommendationInfo: RecommendationInfo;
  /** Call when the guest picks a suggestion that goes STRAIGHT into the cart. */
  onItemSelected: (item: { mid?: string } | string) => void;
  /** Call when a customiser/repeat dialog opened from a suggestion finally adds the item. */
  onItemAdded: (itemId: string) => void;
}

export function useRecommendations(
  options: UseRecommendationsOptions = {},
): UseRecommendationsResult {
  const {
    limit,
    catalogFilter,
    excludeAcceptedCategories = true,
    trackShown = true,
    enabled = true,
  } = options;

  const isAvailable = useSelector(isSuggestiveSellingAvailable);
  const provider = useSelector(getSuggestiveSellingProvider);
  const recommendations = useSelector(selectRecommendations);
  const isFetching = useSelector(selectIsFetchingRecommendations);
  const saleItems = useSelector((state: any) => state.saleDataStore?.saleData?.saleItems);
  const catalogContext = useCatalogContext();

  const recommendationId = recommendations?.recommendationId;
  const isActive = enabled && isAvailable;

  const cartItemIds = useMemo(() => getUniqueCartItemIds(saleItems), [saleItems]);

  // Categories the guest has already accepted from THIS engine response.
  //
  // Reset DURING RENDER rather than in an effect: an effect runs after the commit, so the render
  // that first sees a new recommendationId would select with the previous round's exclusions still
  // applied and briefly show fewer suggestions than it should. Adjusting state during render is
  // React's own answer to "derive state from a changing input" — it re-runs this component before
  // committing anything, so the memo below sees the cleared set on the very first paint.
  const [acceptedCategories, setAcceptedCategories] = useState<ReadonlySet<string>>(EMPTY_CATEGORIES);
  const [lastRecommendationId, setLastRecommendationId] = useState(recommendationId);
  if (lastRecommendationId !== recommendationId) {
    setLastRecommendationId(recommendationId);
    setAcceptedCategories(EMPTY_CATEGORIES);
  }

  const items = useMemo(() => {
    if (!isActive) return [];
    return selectRecommendedItems({
      recommendations,
      catalogContext,
      recommendationProvider: provider,
      cartItemIds,
      excludedCategories: excludeAcceptedCategories ? acceptedCategories : undefined,
      limit,
      catalogFilter,
    });
  }, [
    isActive,
    recommendations,
    catalogContext,
    provider,
    cartItemIds,
    excludeAcceptedCategories,
    acceptedCategories,
    limit,
    catalogFilter,
  ]);

  // Only once suggestions actually reached the screen — a response whose every item the catalogue
  // rejected was never "shown". A collapsible surface opts out here and reports it itself.
  useRecommendationsShown({
    recommendationId,
    isVisible: Boolean(trackShown && isActive && items.length),
  });

  const metaCategoryByItemId = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    items.forEach((item) => {
      map[item.id] = item.classificationCategory;
    });
    return map;
  }, [items]);

  const rememberCategory = useCallback(
    (itemId?: string) => {
      if (!itemId || !excludeAcceptedCategories) return;
      const category = metaCategoryByItemId[itemId];
      if (!category) return;
      setAcceptedCategories((previous) =>
        previous.has(category) ? previous : new Set(previous).add(category),
      );
    },
    [metaCategoryByItemId, excludeAcceptedCategories],
  );

  const onItemSelected = useCallback(
    (item: { mid?: string } | string) => {
      rememberCategory(typeof item === "string" ? item : item?.mid);
    },
    [rememberCategory],
  );

  const recommendationInfo = useMemo(
    () => createRecommendationInfo(recommendations, provider),
    [recommendations, provider],
  );

  return {
    items,
    hasItems: items.length > 0,
    isFetching,
    isAvailable,
    recommendationId,
    provider,
    recommendationInfo,
    onItemSelected,
    onItemAdded: rememberCategory,
  };
}
